function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    newNode.querySelectorAll('legend').forEach((newLegend, index) => {
      let spanContent = newLegend.querySelector('span').innerHTML;
      let stickyLegend = document.querySelectorAll('.sticky-options legend')[index];

      if (stickyLegend) {
        stickyLegend.querySelector('span').textContent = spanContent;
      }
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    if (document.querySelectorAll('body.lock-overflow')) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
      document.querySelectorAll('.size-guide-video .play-btn').forEach((i) => {
        i.click();
      });
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          let relatedSwiper;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class UpsellProduct extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeUpsellProduct(this.dataset.productId);
  }

  initializeUpsellProduct(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const upsellProduct = html.querySelector('upsell-product');

        if (upsellProduct && upsellProduct.querySelectorAll('.cart__upsell').length > 0) {
          this.innerHTML = upsellProduct.innerHTML;
          window.optionEvents();
          new Swiper('#CartDrawer .upsell-product-swiper', {
            slidesPerView: 1.1,
            spaceBetween: 12,
            loop: false,
            pagination: {
              el: '#CartDrawer .swiper-pagination',
              clickable: true,
            },
          });
          document.querySelector('.cart-upsell-section').classList.remove('!tw-hidden');
        } else if (upsellProduct) {
          document.querySelector('.cart-upsell-section').classList.add('!tw-hidden');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}
customElements.define('upsell-product', UpsellProduct);

class AddItem extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick(event) {
    this.classList.add('has-loading');

    let newId = this.dataset.newId;
    let oldId = this.dataset.oldId;

    let updates = {
      [newId]: 1,
      [oldId]: 0,
    };

    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    })
      .then((response) => response.json())
      .then((product) => {
        const drawerSectionId = document.querySelector('cart-drawer').dataset.id;
        fetch(`${routes.cart_url}?section_id=${drawerSectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            document.querySelector('cart-drawer .drawer__inner').innerHTML =
              html.querySelector('cart-drawer .drawer__inner').innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      })
      .catch((error) => console.error('Error:', error));
  }
}
customElements.define('add-item', AddItem);

class QuickShop extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick(event) {
    let dataHandle = this.dataset.handle;
    let $this = this;
    $this.classList.add('has-loading');

    var holder = document.getElementById('TemplateQuickShopHolder');
    var url = '/products/' + dataHandle + '?view=quick-view';

    url = url.replace('//', '/');

    fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        // Convert the HTML string into a document object
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        var div = doc.querySelector('product-info');

        if (!holder) {
          return;
        }

        holder.innerHTML = '';
        holder.append(div);

        $this.classList.remove('has-loading');
        document.querySelector('#TemplateQuickShopModal').classList.add('active');
        document.querySelector('body').classList.add('lock-overflow');

        const button = document.querySelector('#TemplateQuickShopModal .modal-close');
        if (button) {
          button.onclick = function (event) {
            this.closest('#TemplateQuickShopModal').classList.remove('active');
            document.querySelector('body').classList.remove('lock-overflow');
          };
        }
      });
  }
}
customElements.define('quick-shop', QuickShop);

/*
window.freeProductAdd = function (freeProductId, productFreeId) {
  fetch('/cart', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then((response) => response.json())
    .then((cart) => {
      let productExists = false;
      for (i = 0; i < cart.items.length; i++) {
        if (cart.items[i].properties._free_gift) {
          productExists = true;
          break;
        } else {
          productExists = false;
        }
      }

      if (!productExists) {
        // Add free product only if it doesn't exist in cart
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: freeProductId,
            quantity: 1,
            properties: { _free_gift: 'Free Gift' },
          }),
        });
      }
    })
    .then((response) => response && response.json())
    .then((product) => {
      if (product) {
        // Only update drawer if product was added
        const drawerSectionId = document.querySelector('cart-drawer').dataset.id;
        return fetch(`${routes.cart_url}?section_id=${drawerSectionId}`);
      }
    })
    .then((response) => response && response.text())
    .then((responseText) => {
      if (responseText) {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        document.querySelector('cart-drawer .drawer__inner').innerHTML =
          html.querySelector('cart-drawer .drawer__inner').innerHTML;
      }
    })
    .catch((error) => console.error('Error:', error));
};
*/

window.freeProductRemove = function (removeFreeProductId) {
  // Create an updates object with the variant ID set to 0 quantity
  let updates = {
    [removeFreeProductId]: 0,
  };

  // Send request to update cart
  fetch(window.Shopify.routes.root + 'cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ updates }),
  })
    .then((response) => response.json())
    .then((cart) => {
      if (cart.item_count === 0) {
        document.querySelector('cart-drawer').classList.add('is-empty');
      } else if (cart.item_count > 0) {
        document.querySelector('cart-drawer').classList.remove('is-empty');
      }

      const drawerSectionId = document.querySelector('cart-drawer').dataset.id;
      return fetch(`${routes.cart_url}?section_id=${drawerSectionId}`);
    })
    .then((response) => response.text())
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      var cartCountEl = html.querySelector('.cart-count.drawer__title-count');
      var cartCount = cartCountEl ? parseInt(cartCountEl.textContent.match(/\d+/)[0]) : 0;
      const cartBubble = document.querySelector('.cart-count-bubble');
      if (cartBubble) {
        cartBubble.classList.remove('tw-hidden');
        cartBubble.querySelector('span').textContent = cartCount;
      }
      document.querySelector('cart-drawer .drawer__inner').innerHTML =
        html.querySelector('cart-drawer .drawer__inner').innerHTML;
    })
    .catch((error) => console.error('Error:', error));
};

document.addEventListener('DOMContentLoaded', (event) => {
  function updateAnnouncementHeight() {
    var announcement = document.querySelector('.announcement-bar-section');
    if (announcement) {
      var newHeight = announcement.offsetHeight;
      document.documentElement.style.setProperty('--announcment-height', newHeight + 'px');
    }
  }
  updateAnnouncementHeight();
  window.addEventListener('resize', updateAnnouncementHeight);
});

/* Price Range */
var PriceRange = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.rangeLowerBound = this.querySelector('input[type="range"]:first-child');
    this.rangeHigherBound = this.querySelector('input[type="range"]:last-child');
    this.textInputLowerBound = this.querySelector('input[name="filter.v.price.gte"]');
    this.textSpanLowerBound = this.querySelector('.min-value');
    this.textSpanHigherBound = this.querySelector('.max-value');
    this.textInputHigherBound = this.querySelector('input[name="filter.v.price.lte"]');
    this.textInputLowerBound.addEventListener('focus', () => this.textInputLowerBound.select(), {
      signal: this._abortController.signal,
    });
    this.textInputHigherBound.addEventListener('focus', () => this.textInputHigherBound.select(), {
      signal: this._abortController.signal,
    });
    this.textInputLowerBound.addEventListener(
      'change',
      (event) => {
        event.preventDefault();
        event.target.value = Math.max(
          Math.min(parseInt(event.target.value), parseInt(this.textInputHigherBound.value || event.target.max) - 1),
          event.target.min
        );
        this.rangeLowerBound.value = event.target.value;
        this.rangeLowerBound.parentElement.style.setProperty(
          '--range-min',
          `${(parseInt(this.rangeLowerBound.value) / parseInt(this.rangeLowerBound.max)) * 100}%`
        );
      },
      { signal: this._abortController.signal }
    );
    this.textInputHigherBound.addEventListener(
      'change',
      (event) => {
        event.preventDefault();
        event.target.value = Math.min(
          Math.max(parseInt(event.target.value), parseInt(this.textInputLowerBound.value || event.target.min) + 1),
          event.target.max
        );
        this.rangeHigherBound.value = event.target.value;
        this.rangeHigherBound.parentElement.style.setProperty(
          '--range-max',
          `${(parseInt(this.rangeHigherBound.value) / parseInt(this.rangeHigherBound.max)) * 100}%`
        );
      },
      { signal: this._abortController.signal }
    );
    this.rangeLowerBound.addEventListener(
      'change',
      (event) => {
        event.stopPropagation();
        this.textInputLowerBound.value = event.target.value;
        this.textSpanLowerBound.innerHTML = event.target.value;
        this.textInputLowerBound.dispatchEvent(new Event('change', { bubbles: true }));
      },
      { signal: this._abortController.signal }
    );
    this.rangeHigherBound.addEventListener(
      'change',
      (event) => {
        event.stopPropagation();
        this.textInputHigherBound.value = event.target.value;
        this.textSpanHigherBound.innerHTML = event.target.value;
        this.textInputHigherBound.dispatchEvent(new Event('change', { bubbles: true }));
      },
      { signal: this._abortController.signal }
    );
    this.rangeLowerBound.addEventListener(
      'input',
      (event) => {
        event.target.value = Math.min(
          parseInt(event.target.value),
          parseInt(this.textInputHigherBound.value || event.target.max) - 1
        );
        event.target.parentElement.style.setProperty(
          '--range-min',
          `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
        );
        this.textInputLowerBound.value = event.target.value;
      },
      { signal: this._abortController.signal }
    );
    this.rangeHigherBound.addEventListener(
      'input',
      (event) => {
        event.target.value = Math.max(
          parseInt(event.target.value),
          parseInt(this.textInputLowerBound.value || event.target.min) + 1
        );
        event.target.parentElement.style.setProperty('--range-max', '0px');
        this.textInputHigherBound.value = event.target.value;
      },
      { signal: this._abortController.signal }
    );
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
};
if (!window.customElements.get('price-range')) {
  window.customElements.define('price-range', PriceRange);
}

window.optionEvents = function () {
  upsellProductVariant();
  siblingProductVariant();
};
function siblingProductVariant() {
  let buttonOption = document.querySelectorAll('.main-variant-selector-select .sibling-product-card .option-button');
  if (buttonOption) {
    buttonOption.forEach(function (item) {
      item.onclick = function () {
        this.closest('.option-card').classList.toggle('active');
      };
    });
  }

  let optionLists = document.querySelectorAll('.sibling-product-card li');
  if (optionLists) {
    optionLists.forEach(function (item) {
      item.onclick = function () {
        let optionCard = this.closest('.option-card');
        optionCard.querySelectorAll('.option-value li').forEach(function (i) {
          i.classList.remove('active');
        });
        this.classList.add('active');

        if (this.querySelector('.sibling-option-main')) {
          this.closest('.grid__item').querySelector('.right-part .price').textContent = this.dataset.price;
          this.closest('.grid__item').querySelector('.image img').src = this.dataset.img;
          this.closest('.grid__item').querySelector('.right-part a h3').textContent = this.dataset.value;
          this.closest('.grid__item').querySelector('.right-part a').href = this.dataset.link;
          this.closest('.main-variant-selector-select').querySelector('.option-variant-main').innerHTML =
            this.querySelector('.sibling-option-main').innerHTML;
        }

        let buttonOption = optionCard.querySelector('.option-button span');

        if (buttonOption) {
          buttonOption.textContent = this.dataset.value;
          optionCard.classList.remove('active');
        }
        upsellProductVariant();
        this.closest('.main-variant-selector-select')
          .querySelectorAll('.option-variant-main .option-card')
          .forEach((i) => {
            i.querySelector('li').click();
          });
      };
    });
  }
}

function upsellProductVariant() {
  const buttons = document.querySelectorAll('.custom-upsell-add');
  buttons.forEach((button) => {
    button.onclick = function (event) {
      let newId = this.dataset.id;
      this.classList.add('has-loading');
      let $this = this;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: newId,
          quantity: 1,
        }),
      })
        .then((res) => res.json())
        .then(() => {
          var url = `/?section_id=${document
            .querySelector('cart-drawer')
            .parentElement.id.replace('shopify-section-', '')}`;
          fetch(url)
            .then((response) => response.text())
            .then((data) => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(data, 'text/html');
              const newCartDrawer = doc.querySelector('cart-drawer');
              const currentCartDrawer = document.querySelector('cart-drawer');

              if (newCartDrawer && currentCartDrawer) {
                window.freeProductSwatch();
                if ($this.closest('cart-drawer')) {
                  currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
                } else {
                  currentCartDrawer.replaceWith(newCartDrawer);
                  document.getElementById('cart-icon-bubble').click();
                }
              }
            })
            .catch((error) => {
              console.error('Error:', error);
            });
        })
        .catch((err) => console.error(err));
    };
  });

  let optionLists = document.querySelectorAll('.option-variant-main li');
  if (optionLists) {
    optionLists.forEach(function (item) {
      item.onclick = function () {
        let optionCard = this.closest('.option-card');
        optionCard.querySelectorAll('.option-value li').forEach(function (i) {
          i.classList.remove('active');
        });
        this.classList.add('active');

        let buttonOption = optionCard.querySelector('.button-option span');

        if (buttonOption) {
          buttonOption.textContent = this.dataset.value;
          optionCard.classList.remove('active');
        }

        let selectedValues = [];
        this.closest('.main-variant-selector-select')
          .querySelectorAll('.option-variant-main .option-card')
          .forEach((card) => {
            let activeItem = card.querySelector('.option-value li.active');
            if (activeItem) {
              selectedValues.push(activeItem.dataset.value);
            }
          });

        let result = selectedValues.join(' / ');

        const variantContainers = document.querySelectorAll('.variant-selector-main');

        variantContainers.forEach((container) => {
          const optionListItems = container.querySelectorAll('.option-variant-main li');

          optionListItems.forEach((item) => {
            item.addEventListener('click', () => {
              const optionCard = item.closest('.option-card');
              optionCard.querySelectorAll('.option-value li').forEach((li) => li.classList.remove('active'));
              item.classList.add('active');

              // Update visible label
              const buttonOption = optionCard.querySelector('.button-option span');
              if (buttonOption) {
                buttonOption.textContent = item.dataset.value;
                optionCard.classList.remove('active');
              }

              // Get full variant title from active selections
              const selectedValues = [];
              container.querySelectorAll('.option-card').forEach((card) => {
                const active = card.querySelector('li.active');
                if (active) selectedValues.push(active.dataset.value);
              });

              const fullTitle = selectedValues.join(' / ');
              const variantOption = container.querySelector(`.default-select-box option[data-title="${fullTitle}"]`);

              if (variantOption) {
                const variantId = variantOption.dataset.value;
                const variantImg = variantOption.dataset.img;

                const addButton = container.querySelector('.custom-upsell-add');
                addButton.setAttribute('data-id', variantId);

                if (variantOption.disabled) {
                  addButton.classList.add('disabled');
                } else {
                  addButton.classList.remove('disabled');
                }

                // ✅ Update the product image
                const imageEl = container
                  .closest('.cart__upsell-product-tile')
                  .querySelector('.cart__upsell-product-tile-image img');
                if (imageEl && variantImg) {
                  imageEl.setAttribute('src', variantImg);
                }
              }
            });
          });
        });

        this.closest('.main-variant-selector-select')
          .querySelectorAll(`.option-variant-main .default-select-box option[data-title="${result}"]`)
          .forEach((option) => {
            this.closest('.variant-selector-main')
              .querySelector('.custom-upsell-add')
              .setAttribute('data-id', option.dataset.value);
            if (option && option.hasAttribute('disabled')) {
              this.closest('.variant-selector-main').querySelector('.custom-upsell-add').classList.add('disabled');
            } else {
              this.closest('.variant-selector-main').querySelector('.custom-upsell-add').classList.remove('disabled');
            }
          });
      };
    });
  }

  let buttonOption = document.querySelectorAll('.main-variant-selector-select .button-option');
  if (buttonOption) {
    buttonOption.forEach(function (item) {
      item.onclick = function () {
        let optionCard = this.closest('.option-card');

        optionCard.closest('.option-card').classList.toggle('active');
        document.addEventListener('click', function handleOutsideClick(event) {
          if (!optionCard.contains(event.target)) {
            optionCard.classList.remove('active');
            document.removeEventListener('click', handleOutsideClick); // Clean up after running once
          }
        });
      };
    });
  }

  const main = document.querySelectorAll('.product-section');
  main.forEach((button) => {
    button.onclick = function (event) {
      let dropdowns = document.querySelectorAll('.main-variant-selector-select');
      dropdowns.forEach((dropdown) => {
        if (!dropdown.contains(event.target)) {
          dropdown.querySelectorAll('.option-card').forEach((i) => {
            i.classList.remove('active');
          });
        }
      });
    };
  });
}

window.freeProductSwatch = function () {
  const giftOptions = document.querySelectorAll('.free-gift-product-option');
  giftOptions.forEach((option) => {
    option.addEventListener('click', freeGiftProductOptionClick);
  });
};

window.addFirstFreeProduct = () => {
  if (
    document.querySelector('cart-drawer .free-gift-products') &&
    !document.querySelector('cart-drawer .cart-items .has-free-item')
  ) {
    document.querySelector('cart-drawer .free-gift-products .free-gift-product-option').click();
  }
};

document.addEventListener('DOMContentLoaded', function () {
  window.freeProductSwatch();

  document.querySelectorAll('.range_min').forEach((rangeMin) => {
    if (rangeMin.value == 0) {
      rangeMin.value =
        window.location.search.split('filter.v.price.lte=').length > 1
          ? window.location.search.split('filter.v.price.lte=')[1].split('&')[0]
          : 2;
    }
  });

  if (document.querySelector('.price-tooltip') && document.querySelector('.range_min')) {
    document.querySelector('.price-tooltip').textContent = '$' + document.querySelector('.range_min').value;
  }

  if (document.querySelector('[name="filter.v.price.lte"]')) {
    document.querySelector('[name="filter.v.price.lte"]').value =
      document.querySelector('[name="filter.v.price.lte"]').max;
    document
      .querySelector('[name="filter.v.price.lte"]')
      .setAttribute('value', document.querySelector('[name="filter.v.price.lte"]').max);
  }

  if (document.querySelector('price-range')) {
    document
      .querySelector('price-range')
      .updateDisplay(document.querySelector('.range_min').value, document.querySelector('.range_max').value);
  }
});

document.querySelectorAll('.price_range_main').forEach((item) => {
  const maxItem = item.querySelector('.range_max');
  if (maxItem.value === maxItem.max || maxItem.value === '0') {
    const rangeMin = parseInt((2 * 100) / maxItem.max);
    item.style.setProperty('--range-min', `${rangeMin}%`);
  } else {
    const rangeMin = parseInt((maxItem.value * 100) / maxItem.max);
    item.style.setProperty('--range-min', `${rangeMin}%`);
  }
});

window.priceRangeRender = function () {
  const priceRanges = document.querySelectorAll('price-range');

  priceRanges.forEach((priceRange) => {
    const minSlider = priceRange.querySelector('.range_min');
    const maxSlider = priceRange.querySelector('.range_max');
    const minTooltip = priceRange.querySelector('.min-tooltip');
    const maxTooltip = priceRange.querySelector('.max-tooltip');
    const minValueDisplay = priceRange.querySelector('.min-value');
    const maxValueDisplay = priceRange.querySelector('.max-value');
    const currencySymbol = priceRange.querySelector('.prefix').textContent.trim();

    if (minSlider && minTooltip) {
      // Position tooltip initially
      positionTooltip(minSlider, minTooltip);

      // Show tooltip on input
      minSlider.addEventListener('input', function () {
        const value = parseInt(this.value);
        minTooltip.textContent = currencySymbol + value;
        minValueDisplay.textContent = value;
        minTooltip.classList.add('active');
        positionTooltip(minSlider, minTooltip);

        // Update range background
        const rangeGroup = this.closest('.range-group');
        const min = (value / parseInt(this.max)) * 100;
        rangeGroup.style.setProperty('--range-min', min + '%');
      });

      // Hide tooltip when not interacting
      minSlider.addEventListener('mouseup', function () {
        setTimeout(() => {
          minTooltip.classList.remove('active');
        }, 1000);
      });

      minSlider.addEventListener('touchend', function () {
        setTimeout(() => {
          minTooltip.classList.remove('active');
        }, 1000);
      });
    }

    if (maxSlider && maxTooltip) {
      // Position tooltip initially
      positionTooltip(maxSlider, maxTooltip);

      // Show tooltip on input
      maxSlider.addEventListener('input', function () {
        const value = parseInt(this.value);
        maxTooltip.textContent = currencySymbol + value;
        maxValueDisplay.textContent = value;
        maxTooltip.classList.add('active');
        positionTooltip(maxSlider, maxTooltip);

        // Update range background
        const rangeGroup = this.closest('.range-group');
        const max = (value / parseInt(this.max)) * 100;
        rangeGroup.style.setProperty('--range-max', max + '%');
      });

      // Hide tooltip when not interacting
      maxSlider.addEventListener('mouseup', function () {
        setTimeout(() => {
          maxTooltip.classList.remove('active');
        }, 1000);
      });

      maxSlider.addEventListener('touchend', function () {
        setTimeout(() => {
          maxTooltip.classList.remove('active');
        }, 1000);
      });
    }
  });

  // Function to position tooltip based on slider thumb position
  function positionTooltip(slider, tooltip) {
    const value = parseInt(slider.value) || 2;
    const min = parseInt(slider.min);
    const max = parseInt(slider.max);
    const percent = ((value - min) / (max - min)) * 100;

    // Calculate position based on percentage
    const leftPosition = `calc(${percent}%)`;
    tooltip.style.left = leftPosition;
  }

  setTimeout(() => {
    document.querySelectorAll('.range_min').forEach((slider) => {
      const value = parseInt(slider.value) || 2;
      const min = parseInt(slider.min);
      const max = parseInt(slider.max);
      const percent = ((value - min) / (max - min)) * 100;

      const leftPosition = `calc(${percent}%)`;
      document.querySelectorAll('.price-tooltip').forEach((i) => {
        i.style.left = leftPosition;
      });
    });
  }, 1000);
};

window.updatePriceRange = function () {
  if (window.doNotUpdate) return;
  const priceRangeElements = document.querySelectorAll('price-range.facets__price');
  priceRangeElements.forEach((priceRange) => {
    priceRange.querySelector('[name="filter.v.price.lte"]').value =
      priceRange.querySelector('[name="filter.v.price.lte"]').max;
    const currentParams = new URLSearchParams(window.location.search);
    const currentLte = currentParams.get('filter.v.price.lte');
    if (currentLte) {
      priceRange.querySelector('.range_min').value = currentLte;
    }
    priceRange
      .querySelector('.price_range_main')
      .style.setProperty(
        '--range-min',
        `${(priceRange.querySelector('.range_min').value * 100) / priceRange.querySelector('.range_max').max}%`
      );
    priceRange.querySelector('.price-tooltip').textContent = '$' + priceRange.querySelector('.range_min').value;
    priceRange
      .querySelector('.price-tooltip')
      .style.setProperty(
        'left',
        `${(priceRange.querySelector('.range_min').value * 100) / priceRange.querySelector('.range_max').max}%`
      );
  });
};

class BundleQuickShop extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    window.productDataJson = [];
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick(event) {
    let dataHandle = this.dataset.handle;
    let $this = this;
    $this.classList.add('has-loading');

    var holder = document.querySelector('.bundle-quick-modal .bundle-quick-body');
    var url = '/products/' + dataHandle + '?view=bundle-quick-shop';

    url = url.replace('//', '/');

    fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        // Convert the HTML string into a document object
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // console.log('doc', doc);

        var div = doc.querySelector('.bundle-product-quick-section');

        if (!holder) {
          return;
        }

        holder.innerHTML = '';
        holder.append(div);

        $this.classList.remove('has-loading');
        document.querySelector('.bundle-quick-modal').classList.add('active');
        document.querySelector('body').classList.add('lock-overflow');

        let jsonElm = holder.querySelector('#productDataJson');

        if (jsonElm) {
          window.productDataJson = JSON.parse(jsonElm.textContent);
        }

        const checkHorizontalOverflow = () => {
          const scrollElm = document.getElementById('sticky_add_bag_scroll_content');
          if (scrollElm) {
            if (scrollElm.scrollWidth > scrollElm.clientWidth) {
              scrollElm.classList.remove('default-spacing');
              scrollElm.classList.add('scroll-x-spacing');
            } else {
              scrollElm.classList.remove('scroll-x-spacing');
              scrollElm.classList.add('default-spacing');
            }
          }
        };

        const button = document.querySelector('.bundle-quick-modal .bundle-quick-close');
        if (button) {
          button.onclick = function (event) {
            this.closest('.bundle-quick-modal').classList.remove('active');
            document.querySelector('body').classList.remove('lock-overflow');
            checkHorizontalOverflow();
          };
        }

        const qtySelector = holder.querySelector('.bundle-product-qauntity select');

        if (qtySelector) {
          qtySelector.addEventListener('change', (select) => {
            let selectedInput = holder
              .querySelector('.bundle-product-variants')
              .querySelector('input[data-variant-id]');
            let selectedVariantId = selectedInput?.value;

            let variant = window.productDataJson?.variants.find((v) => v.id.toString() === selectedVariantId);
            let qty = select.target.value || 1;

            let buttonPriceElm = holder.querySelector('.bundle-add-to-bag .btn-price');
            if (variant) {
              const vprice = ((variant.price / 100) * qty).toFixed(2);

              if (buttonPriceElm) {
                let formattedPriceNew = vprice % 1 === 0 ? Math.floor(vprice) : vprice;

                buttonPriceElm.textContent = '$' + formattedPriceNew;
              }
            }
          });
        }

        const addToCartElm = holder.querySelector('.bundle-add-to-bag');

        if (addToCartElm) {
          addToCartElm.addEventListener('click', () => {
            let selectedInput = holder.querySelector('.bundle-product-variants input[data-variant-id]');
            let selectedVariantId = selectedInput?.value;

            let variant = window.productDataJson?.variants.find((v) => v.id.toString() === selectedVariantId);

            if (variant) {
              let vId = variant.id;
              let qty = parseInt(qtySelector?.value || 1);
              let price = parseFloat(variant.price / 100);
              let image = variant.featured_image?.src || window.productDataJson.featured_image;
              window.addVariantBag(vId, price, qty, image);
            }

            button.click();
            checkHorizontalOverflow();
          });
        }

        const optionList = holder.querySelector('.product-form-input-style .product-option-list');
        const optionItem = optionList?.querySelector('.product-option-item');
        let loadMoreBtn = holder?.querySelector('.load-more-bundle-swatches');

        if (optionList && optionItem) {
          const variantHeight = optionItem.clientHeight * 2 + 10;

          // Optionally set max-height dynamically

          if (optionList.scrollHeight > variantHeight) {
            loadMoreBtn.classList.add('has-scroll');
          } else {
            loadMoreBtn.classList.remove('has-scroll');
          }
        }

        if (loadMoreBtn) {
          loadMoreBtn.addEventListener('click', (btn) => {
            btn.currentTarget.classList.remove('has-scroll');
            optionList.style.maxHeight = 'none';
          });
        }

        const sizeChart = holder?.querySelector('.size-guide');
        if (sizeChart) {
          sizeChart.onclick = function (event) {
            let modalElm = holder.querySelector('.custom-size-modal');

            modalElm.classList.add('modal-open');
          };
        }

        const sizeChartClose = holder?.querySelector('.custom-size-modal .size-modal-close');
        if (sizeChartClose) {
          sizeChartClose.onclick = function (event) {
            let modalElm = holder.querySelector('.custom-size-modal');

            modalElm.classList.remove('modal-open');
          };
        }
      });
  }
}
customElements.define('bundle-quick-shop', BundleQuickShop);

class ProductVariantSelector extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.selectedOptions = [];
    this.selectedInput = this.closest('.bundle-product-variants').querySelector('input[data-variant-id]');
    this.swiperElm = this.closest('.bundle-product-quick-section').querySelector('.bundle-product-images');
    this.swiperMainElm = this.closest('.bundle-product-quick-section').querySelector('.bundle-product-images .swiper');
    //this.selectedOption = this.querySelector('input[data-option-name="Style"]:checked');
    this.selectedVariantId = this.closest('.bundle-product-variants').querySelector('input[data-selected-variant]');
  }

  connectedCallback() {
    const variantsScript = this.querySelector('#variants-data');
    if (!variantsScript || !variantsScript.textContent.trim()) return;

    this.variants = JSON.parse(variantsScript.textContent);

    this.swiperMainElm = this.closest('.bundle-product-quick-section').querySelector('.bundle-product-images .swiper');
    this.swiperInstance = new Swiper(this.swiperMainElm, {
      slidesPerView: 1,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      spaceBetween: 0,
      loop: false,
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1,
      },
      breakpoints: {
        750: {
          slidesPerView: 2.01,
          spaceBetween: 15,
          pagination: false,
        },
      },
    });

    // Bind event listeners to all inputs inside this element
    const variantInputs = this.querySelectorAll('.product-variant-option');
    variantInputs.forEach((input) => {
      input.addEventListener('change', this.handleChange.bind(this));
    });

    // Run initial match
    this.handleChange();
    setTimeout(() => {
      this.productMedia();
    }, 10);
  }

  handleChange() {
    const checkedInputs = this.querySelectorAll('.product-variant-option:checked');
    this.selectedOptions = Array.from(checkedInputs).map((input) => input.value);

    const matchedVariant = this.getSelectedVariant(this.selectedOptions);

    this.updateInputAvailability();
    if (matchedVariant) {
      this.dispatchEvent(
        new CustomEvent('variantChange', {
          detail: {
            variantId: matchedVariant.id,
            price: matchedVariant.price,
            options: this.selectedOptions,
          },
          bubbles: true,
        })
      );

      const price = (matchedVariant.price / 100).toFixed(2);

      const priceContainer = this.closest('.bundle-product-quick-section').querySelector(
        '.bundle-add-to-bag .btn-price'
      );

      if (priceContainer) {
        const vprice = matchedVariant.price / 100;
        let formattedPriceNew = vprice % 1 === 0 ? Math.floor(vprice) : vprice;
        priceContainer.textContent = '$' + formattedPriceNew;
      }

      if (this.selectedInput) {
        this.selectedInput.value = matchedVariant.id;
        this.selectedInput.dataset.selectedVariant = matchedVariant.id;

        if (
          document.querySelector(`.quick-modal-product-select option[data-id="${matchedVariant.id}"]`).dataset
            .available == 'available'
        ) {
        }
        let cartBtn = document.querySelector('.bundle-add-to-bag');
        let btnTextElm = cartBtn.querySelector('.btn-text');
        if (
          document.querySelector(`.quick-modal-product-select option[data-id="${matchedVariant.id}"]`).dataset
            .available == 'soldout'
        ) {
          if (cartBtn) {
            cartBtn.classList.add('disabled');
            if (btnTextElm) {
              btnTextElm.textContent = 'Sold Out';
            }
          }
        } else {
          cartBtn.classList.remove('disabled');
          if (btnTextElm) {
            btnTextElm.textContent = 'Add To Bag';
          }
        }
      }

      checkedInputs.forEach((item) => {
        let selectedOption = item.value;
        item.closest('.product-form__input').querySelector('[data-selected-value]').textContent = selectedOption;
      });

      const qtySelector = this.closest('.bundle-product-quick-section')?.querySelector(
        '.bundle-product-qauntity select'
      );
      if (qtySelector) {
        qtySelector.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        const price = (matchedVariant.price / 100).toFixed(2);
        const priceContainer = this.closest('.bundle-product-quick-section').querySelector(
          '.bundle-add-to-bag .btn-price'
        );
        if (priceContainer) {
          const vprice = matchedVariant.price / 100;

          let formattedPriceNew = vprice % 1 === 0 ? Math.floor(vprice) : vprice;

          priceContainer.textContent = '$' + formattedPriceNew;
        }
      }
    }

    setTimeout(() => {
      this.productMedia();
    }, 10);
  }

  getSelectedVariant(selectedOptions) {
    return this.variants.find((variant) => {
      return variant.options.every((opt, index) => opt === selectedOptions[index]);
    });
  }

  updateInputAvailability() {
    const optionGroups = this.querySelectorAll('.product-form__input');

    optionGroups.forEach((group, optionIndex) => {
      const inputs = group.querySelectorAll('.product-variant-option');

      inputs.forEach((input) => {
        const testOptions = [...this.selectedOptions];
        testOptions[optionIndex] = input.value;

        const isAvailable = this.variants.some((variant) => {
          return variant.options.every((opt, i) => {
            return testOptions[i] ? testOptions[i] === opt : true;
          });
        });

        input.disabled = !isAvailable;
      });
    });
  }

  productMedia() {
    const variantInput = this.querySelector('input[data-option-name="Style"]:checked');
    const inputSKU = variantInput?.dataset.sku?.toLowerCase();
    const variantSKU = variantInput?.dataset.sku;
    const product = window.productDataJson;

    if (!variantInput || !inputSKU || !product || !this.swiperElm) return;
    const productType = document.querySelector('.bundle-product-images').dataset.productType;
    const slides = this.swiperElm.querySelectorAll('.swiper-slide');

    const imageAlts = Array.from(this.swiperElm.querySelectorAll('.swiper-slide img')).map((img) => img.alt);
    // const allHavePipe = imageAlts.some((alt) => alt.includes('|'));

    slides.forEach((slide) => {
      const img = slide.querySelector('img');
      let skuToCheck = variantSKU;

      if (
        productType === 'Bottom' ||
        productType === 'Bottoms' ||
        productType === 'Long Sleeve Top' ||
        productType === 'Outerwear' ||
        productType === 'Performance Polo' ||
        productType === 'Performance Shirt' ||
        productType === 'Performance Tops' ||
        productType === 'Silicone Ring' ||
        productType === 'Swimwear' ||
        productType === 'T-Shirts' ||
        productType === 'T-Shirt' ||
        productType === 'Tops'
      ) {
        const skuSplit = variantSKU.split('-');
        skuToCheck = skuSplit[0] + '-' + skuSplit[1];
      }

      const imgAltSku = img.alt.split('|')[1];

      if (img && imgAltSku === skuToCheck) {
        // slide.classList.remove('hidden-slide');
        slide.style.display = '';
      } else {
        slide.style.display = 'none';
        // slide.classList.add('hidden-slide');
      }
      // const img = slide.querySelector('img');
      // const alt = img?.alt?.toLowerCase() || '';
      // const altSKU = alt.split('|')[1];
      // if (allHavePipe) {
      //   if (altSKU && inputSKU==altSKU) {
      //     slide.style.display = '';
      //   } else {
      //     slide.style.display = 'none';
      //   }
      // } else {
      //   slide.style.display = '';
      // }
    });

    // Update Swiper (you may want to use `swiper.update()` if needed)
    if (this.swiperElm.querySelector('.swiper')?.swiper) {
      this.swiperElm.querySelector('.swiper').swiper.update();

      this.querySelectorAll('.product-form-input-style input').forEach((input) => {
        input.addEventListener('change', (event) => {
          this.swiperElm.querySelector('.swiper').swiper.slideTo(0);
          // if (allHavePipe) {
          // }

          // let selectedDisabled = event.target.classList.contains('disabled');
          // let parentElement = event.currentTarget.closest('.product-form-input-style');
          // let cartBtn = document.querySelector('.bundle-add-to-bag');
          // let btnTextElm = cartBtn.querySelector('.btn-text');

          // if (selectedDisabled && parentElement) {
          //   if (cartBtn) {
          //     cartBtn.classList.add('disabled');
          //     if (btnTextElm) {
          //       btnTextElm.textContent = 'Sold Out';
          //     }
          //   }
          // } else {
          //   cartBtn.classList.remove('disabled');
          //   if (btnTextElm) {
          //     btnTextElm.textContent = 'Add To Bag';
          //   }
          // }
        });
      });
    }
  }
}
customElements.define('product-variant-selector', ProductVariantSelector);

document.addEventListener('DOMContentLoaded', function () {
  window.bundleBagItem();

  checkHorizontalOverflow();

  const container = document.querySelector('.sticky-add-bag .item-content-main');
  const track = document.querySelector('.sticky-add-bag .custom-scrollbar-track-horizontal');
  const thumb = document.querySelector('.sticky-add-bag .custom-scrollbar-thumb-horizontal');
  if (container && track && thumb) {
    updateThumbWidth();
    updateThumbPosition();
  }
});

window.bundleBagItem = function () {
  const storedData = JSON.parse(localStorage.getItem('bundleData'));

  const container = document.querySelector('.sticky-add-bag .item-content-main');
  if (storedData == null || !container) return;

  container.innerHTML = '';
  const variants = storedData.item.variants;

  let totalPrice = 0;
  let totalQty = 0;

  // Add variant blocks based on quantity
  let checkoutUrl = [];
  variants.forEach((variant) => {
    const quantity = parseInt(variant.quantity) || 1;
    const price = parseFloat(variant.price);
    checkoutUrl.push(variant.id + ':' + quantity);

    totalPrice += price * quantity;
    totalQty += quantity;

    for (let i = 0; i < quantity; i++) {
      const html = `
        <div class="item-content" data-id="${variant.id}" data-price="${price}" data-quantity="${variant.quantity}">
          <img src="${variant.image}">
          <remove-item-bag class="remove-btn"></remove-item-bag>
        </div>
      `;
      container.insertAdjacentHTML('beforeend', html);
    }
  });
  checkoutUrl = checkoutUrl.join(',');
  checkoutUrl = '/cart/' + checkoutUrl;

  // Add blank divs if totalQty < 5
  const blanksNeeded = 5 - totalQty;
  for (let j = 0; j < blanksNeeded; j++) {
    const blankHtml = `<div class="item-content empty"><span class="empty-num">${totalQty + j + 1}</span></div>`;
    container.insertAdjacentHTML('beforeend', blankHtml);
  }

  if (totalQty > 5) {
    container.closest('.item-content-main-scroll').classList.add('scroll-active');
  } else {
    container.closest('.item-content-main-scroll').classList.remove('scroll-active');
  }

  // let discountPercentages = 0;
  // if (parseFloat(document.querySelector('.discount-blocks-main .discount-percentage').dataset.items) > totalQty) {
  //   discountPercentages = document.querySelector('.discount-blocks-main .discount-percentage');
  // } else if (document.querySelector(`.discount-blocks-main .discount-percentage[data-items="${totalQty}"]`)) {
  //   discountPercentages = document.querySelector(
  //     `.discount-blocks-main .discount-percentage[data-items="${totalQty}"]`
  //   );
  // } else if (
  //   parseFloat(document.querySelector('.discount-blocks-main .discount-percentage').dataset.items) < totalQty
  // ) {
  //   const discountBlock = document.querySelectorAll('.discount-blocks-main .discount-percentage');
  //   discountPercentages = discountBlock[discountBlock.length - 1];
  // }

  let discountPercentages = null;
  const percentBlocks = document.querySelectorAll('.discount-blocks-main .discount-percentage');

  percentBlocks.forEach((block) => {
    const item = parseFloat(block.dataset.items);
    if (totalQty >= item) {
      discountPercentages = block;
    }
  });

  let applyDiscount = 0;
  if (discountPercentages) {
    applyDiscount = discountPercentages.dataset.discount;
  }

  const addBagButton = document.querySelector('.sticky-add-bag .add-bag');
  const currencySymbol = addBagButton.dataset.currency || '$';
  const minimumItems = parseInt(addBagButton.dataset.minimumItems) || 1;
  const itemsRemaining = minimumItems - totalQty;
  const discountedPrice = totalPrice - (totalPrice * applyDiscount) / 100;
  if (itemsRemaining <= 0) {
    addBagButton.classList.remove('disabled');
    addBagButton.setAttribute('href', checkoutUrl);
    addBagButton.innerHTML = `Checkout ${totalQty} items • 
    <span>${currencySymbol}${discountedPrice.toFixed(0)}</span> <span class="line-through ${
      applyDiscount === 0 ? 'tw-hidden' : ''
    }"> ${currencySymbol}${totalPrice.toFixed(0)}</span>`;
  } else {
    addBagButton.classList.add('disabled');
    addBagButton.setAttribute('href', 'javascript:void(0);');
    if (totalQty == 0) {
      addBagButton.innerHTML = `Add at least ${itemsRemaining} items to bag`;
    } else {
      addBagButton.innerHTML = `Add at least ${itemsRemaining} items to bag
              <span class="tw-hidden"> • ${currencySymbol}${discountedPrice.toFixed(0)}</span>
              <span class="line-through tw-hidden">${currencySymbol}${totalPrice.toFixed(0)}</span>`;
    }
  }
};

window.updateVariantQuantity = function (variantId, reduceBy) {
  const storedData = JSON.parse(localStorage.getItem('bundleData'));

  if (storedData && storedData.item && Array.isArray(storedData.item.variants)) {
    const variantIndex = storedData.item.variants.findIndex((v) => v.id === variantId);

    if (variantIndex !== -1) {
      const variant = storedData.item.variants[variantIndex];
      const newQuantity = variant.quantity - reduceBy;

      if (newQuantity > 0) {
        variant.quantity = newQuantity;
      } else {
        storedData.item.variants.splice(variantIndex, 1);
      }

      localStorage.setItem('bundleData', JSON.stringify(storedData));
    }
  }
};

class removeItemBag extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick(event) {
    const variantId = parseInt(this.closest('.item-content').dataset.id);
    /* Reduce quantity */
    window.updateVariantQuantity(variantId, 1);
    window.bundleBagItem();

    checkHorizontalOverflow();

    const container = document.querySelector('.sticky-add-bag .item-content-main');
    const track = document.querySelector('.sticky-add-bag .custom-scrollbar-track-horizontal');
    const thumb = document.querySelector('.sticky-add-bag .custom-scrollbar-thumb-horizontal');
    if (container && track && thumb) {
      updateThumbWidth();
      updateThumbPosition();
    }
  }
}
customElements.define('remove-item-bag', removeItemBag);

window.addVariantBag = function (variantId, price, quantity, image) {
  const storedData = JSON.parse(localStorage.getItem('bundleData')) || { item: { variants: [] } };
  const existingVariant = storedData.item.variants.find((v) => v.id === variantId);

  if (existingVariant) {
    existingVariant.quantity += quantity;
    existingVariant.price = price;
    existingVariant.image = image;
  } else {
    storedData.item.variants.push({
      id: variantId,
      price: price,
      quantity: quantity,
      image: image,
    });
  }

  localStorage.setItem('bundleData', JSON.stringify(storedData));

  window.bundleBagItem();

  checkHorizontalOverflow();

  const container = document.querySelector('.sticky-add-bag .item-content-main');
  const track = document.querySelector('.sticky-add-bag .custom-scrollbar-track-horizontal');
  const thumb = document.querySelector('.sticky-add-bag .custom-scrollbar-thumb-horizontal');
  if (container && track && thumb) {
    updateThumbWidth();
    updateThumbPosition();
  }
};
/* bundle tab sticky start */
let lastScrollTop = 0;
const targetElement = document.querySelector('.product-type-tab-back');
if (targetElement) {
  window.addEventListener('scroll', function () {
    let scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop < lastScrollTop) {
      targetElement.classList.add('scroll-up');
    } else {
      targetElement.classList.remove('scroll-up');
    }
    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
  });
}

function updateCombinedHeightVariable() {
  const sectionHeader = document.querySelector('.section-header');
  const sectionHeaderHeight = sectionHeader.offsetHeight;
  document.documentElement.style.setProperty('--combined-height', `${sectionHeaderHeight - 1}px`);
}
window.addEventListener('DOMContentLoaded', updateCombinedHeightVariable);
window.addEventListener('resize', updateCombinedHeightVariable);
window.addEventListener('scroll', updateCombinedHeightVariable);

/* bundle tab sticky end */
const el = document.getElementById('sticky_add_bag_scroll_content');
const checkHorizontalOverflow = () => {
  if (el && el.scrollWidth > el.clientWidth) {
    el.classList.remove('default-spacing');
    el.classList.add('scroll-x-spacing');
  } else if (el) {
    el.classList.remove('scroll-x-spacing');
    el.classList.add('default-spacing');
  }
};
window.addEventListener('resize', checkHorizontalOverflow);
checkHorizontalOverflow();

const container = document.querySelector('.sticky-add-bag .item-content-main');
const track = document.querySelector('.sticky-add-bag .custom-scrollbar-track-horizontal');
const thumb = document.querySelector('.sticky-add-bag .custom-scrollbar-thumb-horizontal');

if (container && track && thumb) {
  function updateThumbWidth() {
    const ratio = container.clientWidth / container.scrollWidth;
    thumb.style.width = `${track.clientWidth * ratio}px`;
  }

  function updateThumbPosition() {
    const scrollRatio = container.scrollLeft / container.scrollWidth;
    const left = scrollRatio * track.clientWidth;
    thumb.style.left = `${left}px`;
  }

  container.addEventListener('scroll', updateThumbPosition);

  window.addEventListener('resize', updateThumbWidth);
  window.addEventListener('load', () => {
    updateThumbWidth();
    updateThumbPosition();
  });

  let isDragging = false;
  let startX;
  let startScrollLeft;

  thumb.addEventListener('mousedown', (e) => {
    isDragging = true;
    startX = e.clientX;
    startScrollLeft = container.scrollLeft;
    document.body.style.userSelect = 'none';
  });

  document.addEventListener('mousemove', (e) => {
    if (!isDragging) return;
    const dx = e.clientX - startX;
    const scrollRatio = container.scrollWidth / track.clientWidth;
    container.scrollLeft = startScrollLeft + dx * scrollRatio;
  });

  document.addEventListener('mouseup', () => {
    isDragging = false;
    document.body.style.userSelect = 'auto';
  });
}

document.addEventListener('DOMContentLoaded', function () {
  window.collectionTabEve();
  window.initializeInterTabObserver();
});

window.collectionTabEve = function () {
  const menuItems = document.querySelectorAll('.tab-anchor');
  menuItems.forEach(function (item) {
    item.addEventListener('click', function () {
      if (document.getElementById(`${this.dataset.href}`)) {
        document.getElementById(`${this.dataset.href}`).scrollIntoView({ behavior: 'smooth' });
      }
    });
  });
};

window.initializeInterTabObserver = function () {
  const items = document.querySelectorAll('.type-product-grid');
  let visibleMap = new Map();

  function getVisibleHeight(entry) {
    const rect = entry.boundingClientRect;
    const top = Math.max(rect.top, 0);
    const bottom = Math.min(rect.bottom, window.innerHeight);
    return Math.max(0, bottom - top);
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const visibleHeight = getVisibleHeight(entry);
          visibleMap.set(entry.target, visibleHeight);
        } else {
          visibleMap.delete(entry.target);
        }
      });

      let mostVisibleEl = null;
      let maxHeight = 0;

      visibleMap.forEach((height, el) => {
        if (height > maxHeight) {
          maxHeight = height;
          mostVisibleEl = el;
        }
      });

      items.forEach((item) => item.classList.remove('active'));
      if (mostVisibleEl && !mostVisibleEl.classList.contains('active')) {
        document.querySelectorAll('.tab-anchor').forEach((item) => item.classList.remove('active'));
        document.querySelector(`.tab-anchor[data-href="${mostVisibleEl.dataset.id}"]`).classList.add('active');
      }
    },
    {
      threshold: Array.from({ length: 101 }, (_, i) => i / 100),
    }
  );

  items.forEach((item) => observer.observe(item));
};

window.addCartItem = function (varId, qty) {
  fetch(`${Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      id: varId,
      quantity: qty,
    }),
  })
    .then((res) => res.json())
    .then((data) => {
      var url = `/?section_id=${document
        .querySelector('cart-drawer')
        .parentElement.id.replace('shopify-section-', '')}`;
      fetch(url)
        .then((response) => response.text())
        .then((data) => {
          var parser = new DOMParser();
          var doc = parser.parseFromString(data, 'text/html');
          var cartCountEl = doc.querySelector('.cart-count.drawer__title-count');
          var cartCount = cartCountEl ? parseInt(cartCountEl.textContent.match(/\d+/)[0]) : 0;
          const cartBubble = document.querySelector('.cart-count-bubble');
          if (cartBubble) {
            cartBubble.classList.remove('tw-hidden');
            cartBubble.querySelector('span').textContent = cartCount;
          }
          var newCartDrawer = doc.querySelector('cart-drawer');
          var currentCartDrawer = document.querySelector('cart-drawer');

          if (newCartDrawer && currentCartDrawer) {
            currentCartDrawer.replaceWith(newCartDrawer);
            window.freeProductSwatch();
            document.getElementById('cart-icon-bubble').click();
          }
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    })
    .catch((err) => console.error(err));
};

window.openCartDrawer = function () {
  const cartDrawer = document.querySelector('cart-drawer');
  fetch(`${Shopify.routes.root}cart.js`)
    .then((res) => res.json())
    .then((cart) => {
      //console.log(cart.item_count);
      if (cartDrawer) {
        if (cart.items.length === 0) {
          cartDrawer.classList.add('is-empty');
        } else {
          cartDrawer.classList.remove('is-empty');
        }
        cartDrawer.classList.add('active');
      }
      const trigger = document.getElementById('cart-icon-bubble');
      if (trigger) {
        trigger.click();
      }
    })
    .catch((err) => {
      console.error('Failed to fetch cart:', err);
    });
};
class AddBundleButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    const selectedInput = document.querySelector('.product-variant-options-main input[type="radio"]:checked');
    if (!selectedInput) {
      console.error('No variant selected.');
      return;
    }

    const vId = Number(selectedInput.dataset.variantId);
    const price = Number(selectedInput.dataset.price);
    const qty = Number(this.dataset.varQty);
    const image = selectedInput.dataset.image;

    if (typeof window.addVariantBag === 'function') {
      window.addVariantBag(vId, price, qty, image);
    } else {
      console.error('addVariantBag function is not defined.');
    }

    const href = this.getAttribute('href');
    if (href) {
      setTimeout(() => {
        window.location.href = href;
      }, 50);
    }
  }
}

customElements.define('add-bundle-button', AddBundleButton);
